import { SelectRow } from "@/components/ui/select-row";
import type { Child } from "@spork/s4p-server-sdk";
import { useRouter } from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import { toastError, toastSuccess } from "@/components/AppToast";
import { Button } from "@/components/ui/button";
import { DateTimeRow } from "@/components/ui/date-time-row";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/text";
import { Textarea } from "@/components/ui/textarea";
import { joinChildLabels } from "@/features/selectedChild";
import { parseIsoDate } from "@/lib/date";
import { showAlert } from "@/lib/showAlert";
import { log } from "@/logger";
import { useCreatePickupRequestMutation } from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

import { DaySelectRow, ISO_SCHOOL_WEEKDAYS } from "../../components/ui/day-select-row";
import { Separator } from "../../components/ui/separator";

type PickupRequestFormProps = {
    selectedChildren: Child[];
};

/**
 * Box chrome on a wrapper; TextInput itself stays height-auto so iOS can
 * vertically align placeholder and value the same way.
 * Use min-h-10 (not h-10) so Android font scaling can grow the field instead
 * of clipping placeholder/value at the top.
 */
const INPUT_SHELL_CLASS_NAME =
    "min-h-10 justify-center rounded-md border border-input bg-background px-3 py-2 shadow-sm shadow-black/5";

function getTodayStart(): Date {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
}

function getErrorMessage(error: unknown, fallback: string): string {
    if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof error.message === "string"
    ) {
        return error.message;
    }
    return fallback;
}

function navigateBackOrHome(router: ReturnType<typeof useRouter>) {
    if (router.canGoBack()) {
        router.back();
        return;
    }

    router.replace("/");
}

export function PickupRequestForm({
    selectedChildren,
}: PickupRequestFormProps) {
    const { t } = useTranslation();
    const router = useRouter();
    const [createPickupRequest, { isLoading: isSubmitting }] =
        useCreatePickupRequestMutation();
    const [repeat, setRepeat] = useState<"one-off" | "daily" | "weekly">("one-off");
    const [pickupDate, setPickupDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [daysOfWeek, setDaysOfWeek] = useState<number[]>([]);
    const [pickerName, setPickerName] = useState("");
    const [relationship, setRelationship] = useState("");
    const [note, setNote] = useState("");
    const [today] = useState(getTodayStart);
    const endMinimumDate = useMemo(() => {
        const start = parseIsoDate(pickupDate);
        return start && start > today ? start : today;
    }, [pickupDate, today]);
    const isRecurring = repeat !== "one-off";
    const canSubmit =
        selectedChildren.length > 0 &&
        pickupDate.trim().length > 0 &&
        pickerName.trim().length > 0 &&
        relationship.trim().length > 0 &&
        (repeat !== "weekly" || daysOfWeek.length > 0);

    function handleRepeatChange(value: string) {
        if (value === "daily") {
            setRepeat("daily");
            setDaysOfWeek([...ISO_SCHOOL_WEEKDAYS]);
            return;
        }

        if (value === "weekly") {
            setRepeat("weekly");
            setDaysOfWeek([]);
            return;
        }

        setRepeat("one-off");
        setEndDate("");
        setDaysOfWeek([]);
    }

    function toggleWeekday(days: string[]) {
        setDaysOfWeek(days.map((day) => Number(day)).sort((a, b) => a - b));
    }

    function getValidationError(): string | null {
        if (selectedChildren.length === 0) {
            return t("pickup.studentsRequired");
        }
        if (!pickupDate.trim()) {
            return t("pickup.dateRequired");
        }
        if (repeat === "weekly" && daysOfWeek.length === 0) {
            return t("pickup.daysRequired");
        }
        if (endDate.trim() && endDate.trim() < pickupDate.trim()) {
            return t("pickup.endBeforeStart");
        }
        if (!pickerName.trim()) {
            return t("pickup.pickerNameRequired");
        }
        if (!relationship.trim()) {
            return t("pickup.relationshipRequired");
        }
        return null;
    }

    async function handleSubmit() {
        const validationError = getValidationError();
        if (validationError) {
            showAlert(t("pickup.errorTitle"), validationError);
            return;
        }

        try {
            await createPickupRequest({
                child_id: selectedChildren.map((child) => child.id),
                pickup_date: pickupDate.trim(),
                picker_name: pickerName,
                relationship,
                note: note.trim() || null,
                ...(isRecurring
                    ? {
                          end_date: endDate.trim() || null,
                          days_of_week: daysOfWeek,
                      }
                    : {
                          end_date: pickupDate.trim(),
                          days_of_week: null,
                      }),
            }).unwrap();
        } catch (error) {
            log.error("createPickupRequest failed", error);
            toastError(
                t("pickup.errorTitle"),
                getErrorMessage(error, t("pickup.submitFailed"))
            );
            return;
        }

        toastSuccess(
            t("pickup.submittedTitle"),
            t("pickup.submittedBody", {
                name: joinChildLabels(selectedChildren),
            })
        );
        navigateBackOrHome(router);
    }

    return (
        <View className="gap-4">
            {/* <Text variant="muted">{t("pickup.formHint")}</Text> */}
            <Separator />
            <View className="gap-4">
                <DateTimeRow
                    label={
                        isRecurring ? t("pickup.startDate") : t("pickup.date")
                    }
                    dateValue={pickupDate}
                    onDateChange={setPickupDate}
                    dateTestID={TEST_IDS[TEST_IDS.PICKUP_REQUEST_DATE_FIELD]}
                    minimumDate={today}
                />
                <SelectRow
                    label={t("ui.repeat")}
                    value={repeat}
                    options={[
                        {
                            label: t("ui.oneOff"),
                            value: "one-off",
                        },
                        {
                            label: t("ui.daily"),
                            value: "daily",
                        },
                        {
                            label: t("ui.recurring"),
                            value: "weekly",
                        },
                    ]}
                    onChange={handleRepeatChange}
                    testID={TEST_IDS[TEST_IDS.PICKUP_REQUEST_REPEAT_FIELD]}
                />
                {repeat === "weekly" && (
                    <DaySelectRow
                        daysOfWeek={daysOfWeek}
                        toggleWeekday={toggleWeekday}
                        testIDPrefix={
                            TEST_IDS[TEST_IDS.PICKUP_REQUEST_WEEKDAY_CHIP]
                        }
                    />
                )}
                {isRecurring ? (
                    <DateTimeRow
                        label={t("pickup.endDate")}
                        dateValue={endDate}
                        onDateChange={setEndDate}
                        datePlaceholder={t("pickup.endDateOptional")}
                        dateTestID={
                            TEST_IDS[TEST_IDS.PICKUP_REQUEST_END_DATE_FIELD]
                        }
                        minimumDate={endMinimumDate}
                    />
                ) : null}
            </View>
            <Separator />
            <View className="gap-2">
                <View className="gap-2">
                    <Label>{t("pickup.pickerName")}</Label>
                    <View className={INPUT_SHELL_CLASS_NAME}>
                        <Input
                            placeholder={t("pickup.pickerNamePlaceholder")}
                            value={pickerName}
                            onChangeText={setPickerName}
                            autoCapitalize="words"
                            testID={
                                TEST_IDS[
                                    TEST_IDS.PICKUP_REQUEST_PICKER_NAME_FIELD
                                ]
                            }
                        />
                    </View>
                </View>

                <View className="gap-2">
                    <Label>{t("pickup.relationship")}</Label>
                    <View className={INPUT_SHELL_CLASS_NAME}>
                        <Input
                            placeholder={t("pickup.relationshipPlaceholder")}
                            value={relationship}
                            onChangeText={setRelationship}
                            autoCapitalize="sentences"
                            testID={
                                TEST_IDS[
                                    TEST_IDS.PICKUP_REQUEST_RELATIONSHIP_FIELD
                                ]
                            }
                        />
                    </View>
                </View>
            </View>
            <Separator />
            <View className="gap-2">
                <View className="flex-row justify-between align-center gap-2">
                    <Label>{t("pickup.note")}</Label>
                    <Text variant="muted">({t("ui.optional")})</Text>
                </View>
                <Textarea
                    className="max-h-[200px]"
                    placeholder={t("pickup.notePlaceholder")}
                    value={note}
                    onChangeText={setNote}
                    testID={TEST_IDS[TEST_IDS.PICKUP_REQUEST_NOTE_FIELD]}
                />
            </View>

            <Button
                onPress={handleSubmit}
                disabled={isSubmitting || !canSubmit}
                testID={TEST_IDS[TEST_IDS.PICKUP_REQUEST_SUBMIT_BUTTON]}
            >
                <Text className="text-primary-foreground">
                    {isSubmitting ? t("pickup.submitting") : t("pickup.submit")}
                </Text>
            </Button>
        </View>
    );
}
